import { MINIAPP_PACKAGE_ID, MINIAPP_PACKAGE_IDS } from '@/consts/configs';
import { getContext } from '@/context';

/**
 * Destination names the sheet accepts in `excludedSocials` / `includedSocials`;
 * an unknown one never renders. Only the ones this miniapp has an opinion about
 * are listed — `download` is absent because it's the Download *button*, not a
 * destination, so `hideDownloadButton` is the only lever for it. `one_hour_photo`
 * is separate from `export_to_print` despite both being print flows: excluding
 * the latter alone left "1 Hour Photo" on screen.
 */
export const ShareTouchpoints = {
  ShareLink: 'share_link',
  PublicViewLink: 'public_view_link',
  Present: 'present',
  PresentationLink: 'presentation_link',
  PublishAsWebsite: 'publish_as_website',
  ExportToPrint: 'export_to_print',
  OneHourPhoto: 'one_hour_photo',
  Challenges: 'challenges',
  PostToPicsart: 'post_to_picsart',
  Earn: 'earn',
  Instagram: 'instagram',
  Tiktok: 'tiktok',
  Youtube: 'youtube',
} as const;

/**
 * These four are hidden unless opted in. `youtube` additionally renders only for
 * a video url, and native hosts hide all of them regardless of this list.
 */
export const RESULT_INCLUDED_SOCIALS: readonly string[] = [
  ShareTouchpoints.Instagram,
  ShareTouchpoints.Tiktok,
  ShareTouchpoints.Youtube,
];

/**
 * Never offered from this miniapp, whatever is being shared. `POPUP` mode
 * already drops these plus `present` and `connector` on the sheet's own side
 * (`screenModeFactory.getPopupParams`), so this is belt-and-braces against a
 * screen-mode change rather than the only thing hiding them.
 */
export const BASE_EXCLUDED_SOCIALS: readonly string[] = [
  ShareTouchpoints.PublicViewLink,
  ShareTouchpoints.ExportToPrint,
  ShareTouchpoints.PublishAsWebsite,
];

/** Originating-app metadata, surfaced by the post-to-Picsart destination. */
export interface ExternalApp {
  id: string;
  type: 'NATIVE' | 'MINIAPP';
  version: string;
  payload: Record<string, string>;
}

export interface ExternalData {
  /** URL of the file (or link) to export. */
  url: string;
  /** Download filename. Pointless when `hideDownloadButton` is set. */
  name?: string;
  /** Picsart file uid — drives previews, public links and collaboration. */
  fileUid?: string;
  app?: ExternalApp;
}

/**
 * Canonical keys only. The sheet's `migrateSharedStorageData` still normalizes
 * the legacy `external` / `externalUrl` / `excludeMiniapps` / `includeSocials`
 * spellings, so both work — but nothing here validates the payload, and a
 * mistyped key fails silently with the sheet rendering the wrong destinations.
 * `aiGenerated`, which the previous payload sent, is absent from the sheet's
 * `ExternalData` and unread anywhere in it, so it was dropped.
 */
interface OpenExportShareParams {
  /**
   * Files to export. The **first** entry is the primary one — social share,
   * public link and analytics are single-file and read only that. Extra entries
   * batch into one zipped download.
   */
  externals: ExternalData[];
  /** Opt-in destinations, appended to the end of the share section. */
  includedSocials?: readonly string[];
  /** Destinations to hide. Omitted shows everything available. */
  excludedSocials?: readonly string[];
  /** Hides the Download button — the only lever for it. */
  hideDownloadButton?: boolean;
  /**
   * Hides the sheet's own Copy link button. Needed whenever the share has no
   * `fileUid`: that button copies a Drive public-invitation URL and renders
   * permanently disabled without one. Some screen modes hide it anyway.
   */
  hideCopyLink?: boolean;
  /** Suppresses the iOS/Android auto-download that fires when the sheet opens. */
  skipAutoDownload?: boolean;
}

// The sheet also accepts `horizontalButtons`, deliberately unused: it publishes
// their clicks to the platform, which never relays them back to the caller. See
// docs/guides/preset-sharing.md.

/**
 * Opens the Picsart export-and-share sheet, and the sole owner of the
 * `exportMiniapp` contract — the result share (`useShareFlow`) and the preset
 * share (`features/preset-sharing`) both go through here so the two can't drift.
 *
 * `EXPORT_SHARE`, not `SOCIAL_SHARE`: the latter is the single-network OAuth
 * flow that `socialShareHandler` resumes on the way back.
 */
const openExportShare = async ({
  externals,
  includedSocials,
  excludedSocials,
  hideDownloadButton,
  hideCopyLink,
  skipAutoDownload,
}: OpenExportShareParams): Promise<void> => {
  const context = getContext();
  const state = context.general.sharedStorage.getState();

  await context.general.sharedStorage.dispatch({
    ...state,
    exportMiniapp: {
      externals,
      screenMode: 'POPUP',
      touchpoint: MINIAPP_PACKAGE_ID,
      theme: 'dark',
      ...(includedSocials && { includedSocials: [...includedSocials] }),
      ...(excludedSocials && { excludedSocials: [...excludedSocials] }),
      ...(hideDownloadButton && { hideDownloadButton }),
      ...(hideCopyLink && { hideCopyLink }),
      ...(skipAutoDownload && { skipAutoDownload }),
    },
  });

  await context.handlers.redirectToPackageId(MINIAPP_PACKAGE_IDS.EXPORT_SHARE, {
    comeback: true,
    modal: true,
  });
};

export default openExportShare;
